import React from 'react';
import { useSelfCamera, type CameraStatus } from './camera';
import { Brand, Transcript, formatTime } from './components';
import { candidateCvFor, useContent } from './content';
import { CameraIcon, MicIcon, MicOffIcon, SendIcon, StopIcon } from './icons';
import { Markdown } from './markdown';
import { PHASE_CHIP, PHASE_SHORT, phaseMinutes, type RolePlayPhase } from './roleplay';
import { ROLE_LABELS, SCREENING_CHECKLIST } from './scenarios';
import { SCENARIOS, isRolePlay, type InterviewSetup, type TranscriptEntry } from './types';

const TANIA_SCENARIOS = new Set(['roleplay_screening', 'tania_followup', 'ta_competency']);

/** "Jane Doe" from a CV whose first line is the candidate's name; null when it doesn't look like a name. */
export function candidateNameFromCv(cv: string | undefined): string | null {
  const line = (cv ?? '').split('\n').map((item) => item.replace(/^[#*\s]+|[*\s]+$/gu, '')).find(Boolean);
  if (!line || line.length > 40 || /[\d@:|]/u.test(line) || line.split(/\s+/u).length > 5) return null;
  return line.toLowerCase().replace(/(^|[\s-])\p{L}/gu, (match) => match.toUpperCase());
}

function AiTile({
  monogram,
  muted,
  nameplate,
  speaking,
  status,
  usePhoto,
}: {
  monogram: string;
  muted: boolean;
  nameplate: string;
  speaking: boolean;
  status: string;
  usePhoto: boolean;
}) {
  const [photoFailed, setPhotoFailed] = React.useState(false);
  return (
    <div className={`tile tile--ai ${speaking ? 'is-speaking' : ''} ${muted ? 'is-muted' : ''}`}>
      {usePhoto && !photoFailed ? (
        <>
          {/* A small photo is shown contained over a blurred copy of itself so it is never blown up. */}
          <img alt="" aria-hidden="true" className="tile-photo-bg" src="/api/content/tania-photo" />
          <img alt="" className="tile-photo" onError={() => setPhotoFailed(true)} src="/api/content/tania-photo" />
        </>
      ) : (
        <span className="tile-initials" aria-hidden="true">{monogram}</span>
      )}
      <div className="tile-nameplate">
        <strong>{nameplate}</strong>
        <small>{status}</small>
      </div>
    </div>
  );
}

function SelfTile({
  level,
  muted,
  onToggle,
  status,
  stream,
}: {
  level: number;
  muted: boolean;
  onToggle: () => void;
  status: CameraStatus;
  stream: MediaStream | null;
}) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  React.useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);
  const message =
    status === 'denied'
      ? 'Camera blocked'
      : status === 'unavailable'
        ? 'No camera available'
        : status === 'starting'
          ? 'Starting camera…'
          : 'Camera off';
  return (
    <div className={`tile tile--self ${!muted && level > 0.06 ? 'is-talking' : ''}`}>
      {status === 'on' ? (
        <video autoPlay className="tile-video" muted playsInline ref={videoRef} />
      ) : (
        <span className="tile-placeholder">
          <CameraIcon />
          <small>{message}</small>
          {(status === 'denied' || status === 'unavailable') && (
            <button onClick={onToggle} type="button">Try again</button>
          )}
        </span>
      )}
      <div className="tile-nameplate"><strong>You</strong></div>
    </div>
  );
}

type Tab = 'transcript' | 'notes' | 'reference';

export function InterviewRoom({
  elapsed,
  error,
  interviewerSpeaking,
  micLevel,
  muted,
  notes,
  onEnd,
  onEndRoleplay,
  onInterrupt,
  onMute,
  onNotes,
  onSendText,
  onStartRoleplay,
  phase,
  phaseById,
  phaseStartedAt,
  rpPhase,
  setup,
  transcript,
}: {
  elapsed: number;
  error: string;
  interviewerSpeaking: boolean;
  micLevel: number;
  muted: boolean;
  notes: string;
  onEnd: () => void;
  onEndRoleplay: () => void;
  onInterrupt: () => void;
  onMute: () => void;
  onNotes: (notes: string) => void;
  onSendText: (message: string) => void;
  onStartRoleplay: () => void;
  phase: 'active' | 'ending';
  phaseById: Record<string, RolePlayPhase>;
  phaseStartedAt: number;
  rpPhase: RolePlayPhase;
  setup: InterviewSetup;
  transcript: TranscriptEntry[];
}) {
  const [message, setMessage] = React.useState('');
  const camera = useSelfCamera();
  const [tab, setTab] = React.useState<Tab>(isRolePlay(setup.scenario) ? 'reference' : 'notes');
  // The transcript is hidden by default in every new room; the choice lasts only for this session.
  const [showTranscript, setShowTranscript] = React.useState(false);
  const [checked, setChecked] = React.useState<ReadonlySet<string>>(new Set());
  const labels = ROLE_LABELS[setup.scenario];
  const rolePlay = isRolePlay(setup.scenario);
  const cv = candidateCvFor(setup, useContent());
  const scenario = SCENARIOS.find((item) => item.value === setup.scenario);
  const monogram = rolePlay ? 'AI' : TANIA_SCENARIOS.has(setup.scenario) ? 'TA' : 'AI';
  const remaining = Math.max(0, setup.durationMinutes * 60_000 - elapsed);
  const plan = phaseMinutes(setup.durationMinutes);
  const phaseElapsed = Date.now() - phaseStartedAt;
  const phaseLeft = rpPhase === 3 ? remaining : Math.max(0, plan[rpPhase] * 60_000 - phaseElapsed);
  const phaseOver = rpPhase === 1 && phaseElapsed > plan[1] * 60_000;
  const labelFor = (entry: TranscriptEntry): string => {
    if (entry.role === 'candidate') return labels.user;
    if (!rolePlay) return labels.ai;
    return (phaseById[entry.id] ?? rpPhase) === 2 ? 'Candidate' : 'Tania';
  };
  const candidateName =
    setup.candidateProfile === 'official_tania_min' ? 'Tania Min' : candidateNameFromCv(cv?.text);
  const nameplate =
    setup.scenario === 'tania_followup' || setup.scenario === 'ta_competency'
      ? 'Tania Pershyna · TA Manager, Ideals'
      : rolePlay && rpPhase !== 2
        ? 'Tania Pershyna · TA Manager, Ideals'
        : rolePlay
          ? `Tania (as ${candidateName ? `candidate ${candidateName}` : 'the candidate'})`
        : labels.aiName;
  const tabs: Array<{ id: Tab; label: string }> = [
    ...(rolePlay ? [{ id: 'reference' as const, label: 'CV & checklist' }] : []),
    { id: 'notes', label: 'Notes' },
    { id: 'transcript', label: 'Transcript' },
  ];

  return (
    <main className="room-page">
      <header className="room-header">
        <Brand compact />
        <div className="room-status">
          <span className="live-badge"><i /> Live</span>
          {rolePlay && (
            <span className="phase-chip" data-phase={rpPhase}>
              <b className="chip-full">{PHASE_CHIP[rpPhase]}</b>
              <b className="chip-short">{PHASE_SHORT[rpPhase]}</b> {formatTime(phaseLeft)}
            </span>
          )}
          <span>{formatTime(elapsed)} elapsed</span>
          <span className="room-remaining">{formatTime(remaining)} left</span>
        </div>
        <button className="end-button" disabled={phase === 'ending'} onClick={onEnd} type="button">
          <StopIcon />
          {phase === 'ending' ? 'Wrapping up…' : 'End & debrief'}
        </button>
      </header>

      <section className="room-grid">
        <div className="conversation-stage">
          <div className="stage-meta">
            <span>{scenario?.label}</span>
            <span>·</span>
            <span>{setup.difficulty.replace('_', ' ')}</span>
          </div>
          <div className="tiles">
            <AiTile
              monogram={monogram}
              muted={muted}
              nameplate={nameplate}
              speaking={interviewerSpeaking}
              status={interviewerSpeaking ? 'Speaking' : muted ? 'Your microphone is muted' : 'Listening'}
              usePhoto={TANIA_SCENARIOS.has(setup.scenario)}
            />
            <SelfTile level={micLevel} muted={muted} onToggle={camera.toggle} status={camera.status} stream={camera.stream} />
          </div>
          {error && <div className="room-error" role="alert">{error}</div>}
          <div className="room-controls">
            <button className={muted ? 'is-active' : ''} onClick={onMute} type="button">
              {muted ? <MicOffIcon /> : <MicIcon />}
              <span>{muted ? 'Unmute' : 'Mute'}</span>
            </button>
            <button
              aria-pressed={camera.status === 'on'}
              className={camera.status === 'on' ? '' : 'is-active'}
              onClick={camera.toggle}
              type="button"
            >
              <CameraIcon />
              <span>{camera.status === 'on' ? 'Camera on' : 'Camera off'}</span>
            </button>
            {interviewerSpeaking && (
              <button onClick={onInterrupt} type="button">
                <StopIcon />
                <span>Pause {rolePlay ? (rpPhase === 2 ? 'candidate' : 'Tania') : labels.aiName.toLowerCase()}</span>
              </button>
            )}
          </div>
          {rolePlay && rpPhase === 1 && (
            <>
              <button className="primary-button phase-button" onClick={onStartRoleplay} type="button">
                Start role play
              </button>
              {phaseOver && <p className="phase-hint">Intro is running over — start the role play when you are ready.</p>}
            </>
          )}
          {rolePlay && rpPhase === 2 && (
            <button className="primary-button primary-button--coral phase-button" onClick={onEndRoleplay} type="button">
              End screening call → Q&amp;A
            </button>
          )}
          {rolePlay && rpPhase === 3 && (
            <button className="primary-button phase-button" onClick={onEnd} type="button">
              Finish &amp; get feedback
            </button>
          )}
          <p className="room-hint">Speak naturally. You can interrupt at any time.</p>
        </div>

        <aside className="room-sidebar">
          <div className="sidebar-tabs" role="tablist">
            {tabs.map((item) => (
              <button
                aria-selected={tab === item.id}
                className={tab === item.id ? 'is-active' : ''}
                key={item.id}
                onClick={() => setTab(item.id)}
                role="tab"
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>

          {tab === 'transcript' && !showTranscript && (
            <section className="transcript-panel transcript-hidden">
              <p className="notes-hint">The transcript is hidden so you can stay in the conversation.</p>
              <button className="transcript-toggle" onClick={() => setShowTranscript(true)} type="button">Show transcript</button>
            </section>
          )}
          {tab === 'transcript' && showTranscript && (
            <section className="transcript-panel">
              <button className="transcript-toggle" onClick={() => setShowTranscript(false)} type="button">Hide transcript</button>
              <Transcript
                emptyText="The live transcript will appear here once the conversation begins."
                entries={transcript}
                labelFor={labelFor}
                labels={{ ai: labels.ai, user: labels.user }}
              />
            </section>
          )}
          {tab === 'notes' && (
            <section className="notes-panel">
              <p className="notes-hint">Private notes — they stay in this browser and are used for your debrief.</p>
              <textarea
                aria-label="Private notes"
                onChange={(event) => onNotes(event.target.value)}
                placeholder="Capture numbers, red flags, the CARE answer, follow-ups to ask…"
                value={notes}
              />
            </section>
          )}
          {tab === 'reference' && rolePlay && (
            <section className="reference-panel">
              <h3>Screening checklist</h3>
              <ul className="checklist">
                {SCREENING_CHECKLIST.map((item) => (
                  <li key={item.id}>
                    <label>
                      <input
                        checked={checked.has(item.id)}
                        onChange={(event) => {
                          setChecked((current) => {
                            const next = new Set(current);
                            if (event.target.checked) next.add(item.id);
                            else next.delete(item.id);
                            return next;
                          });
                        }}
                        type="checkbox"
                      />
                      <span>{item.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <h3>Candidate CV</h3>
              {cv ? <Markdown className="md--dark" source={cv.text} /> : <p className="notes-hint">No candidate CV attached.</p>}
            </section>
          )}

          <form
            className="message-composer"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = message.trim();
              if (!trimmed) return;
              onSendText(trimmed);
              setMessage('');
            }}
          >
            <input
              aria-label="Send text to the AI"
              maxLength={800}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Type context or a follow-up…"
              value={message}
            />
            <button aria-label="Send" disabled={!message.trim()} type="submit"><SendIcon /></button>
          </form>
        </aside>
      </section>
    </main>
  );
}
